/**
 * Navbar — Redesigned for a premium, logistics-themed corporate experience
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion';
import {
  FaPhone, FaEnvelope, FaFemale,
  FaArrowRight, FaLinkedinIn, FaFacebookF, FaInstagram, FaYoutube
} from 'react-icons/fa';
import logoImg from '../../assets/images/logo.png';

const NAV_H = 72; // px
const SCROLL_THR = 60; // px before solid bg kicks in

const LINKS = [
  { label: 'Home',     path: '/'         },
  { label: 'About',    path: '/about'    },
  { label: 'Services', path: '/services' },
  { label: 'Contact',  path: '/contact'  },
];

const SOCIALS = [
  { icon: FaLinkedinIn, href: 'https://linkedin.com',  label: 'LinkedIn'  },
  { icon: FaFacebookF,  href: 'https://facebook.com',  label: 'Facebook'  },
  { icon: FaInstagram,  href: 'https://instagram.com', label: 'Instagram' },
  { icon: FaYoutube,    href: 'https://youtube.com',   label: 'YouTube'   },
];

/* Underline that follows active / hovered link */
const Underline = ({ ulRef, idx }) => {
  const [p, setP] = useState({ left: 0, width: 0, opacity: 0 });

  const snap = useCallback((i) => {
    const ul = ulRef.current;
    if (!ul || i == null || i < 0) { setP(v => ({ ...v, opacity: 0 })); return; }
    const lis = Array.from(ul.children).filter(el => el.tagName === 'LI');
    const li = lis[i];
    if (!li) return;
    const ur = ul.getBoundingClientRect();
    const lr = li.getBoundingClientRect();
    const pad = lr.width * 0.25;
    setP({ left: lr.left - ur.left + pad, width: lr.width - pad * 2, opacity: 1 });
  }, [ulRef]);

  useEffect(() => { snap(idx); }, [idx, snap]);

  return (
    <motion.span
      aria-hidden
      animate={p}
      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
      style={{
        position: 'absolute', bottom: 12, height: 1.5, borderRadius: 1,
        background: 'var(--orange)',
        pointerEvents: 'none',
      }}
    />
  );
};



/* 3-bar animated hamburger */
const Burger = ({ open }) => (
  <div style={{ width: 18, height: 12, position: 'relative', pointerEvents: 'none' }}>
    {[0, 1, 2].map(i => (
      <motion.span key={i}
        animate={open
          ? i === 0 ? { y: 5,  rotate: 45,  opacity: 1 }
          : i === 1 ? { opacity: 0 }
          :           { y: -5, rotate: -45, opacity: 1 }
          : { y: 0, rotate: 0, opacity: 1 }
        }
        transition={{ duration: 0.22, ease: 'easeInOut' }}
        style={{
          display: 'block', position: 'absolute',
          top: i === 0 ? 0 : i === 1 ? 5 : 10,
          left: 0, right: 0, height: 1.5,
          background: '#fff', transformOrigin: 'center',
        }}
      />
    ))}
  </div>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(null);
  const location = useLocation();
  const ulRef = useRef(null);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, 'change', y => setScrolled(y > SCROLL_THR));
  useEffect(() => { setMenuOpen(false); }, [location]);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const activeIdx = LINKS.findIndex(l =>
    l.path === '/' ? location.pathname === '/' : location.pathname.startsWith(l.path)
  );

  const isHome = location.pathname === '/';
  const showSolid = scrolled || !isHome;

  return (
    <>
      <motion.header
        initial={{ y: -NAV_H - 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0,
          height: NAV_H, zIndex: 200,
          transition: 'background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
          background: showSolid ? 'var(--navy-deep)' : 'rgba(4, 9, 24, 0.25)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: showSolid ? '1px solid var(--orange-border)' : '1px solid rgba(255,255,255,0.06)',
          boxShadow: showSolid ? '0 10px 30px rgba(0,0,0,0.2)' : 'none',
        }}
      >
        <div className="container-xxl px-4 px-lg-5 h-100">
          <div style={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'space-between' }}>

            {/* LOGO CONTAINER */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem' }}>
              <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.9rem' }}>
                <img
                  src={logoImg}
                  alt="ISAAC Logistic Solutions India Pvt. Ltd. Logo"
                  style={{
                    height: 52, width: 'auto', objectFit: 'contain', display: 'block',
                  }}
                />
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 800,
                    fontSize: 'clamp(0.85rem, 1.5vw, 1.15rem)',
                    color: '#fff',
                    letterSpacing: '0.5px',
                    whiteSpace: 'nowrap'
                  }}>
                    {/* Shortened brand name on mobile / tablet / small laptop */}
                    <span className="d-inline d-xl-none">ISAAC Logistics</span>
                    {/* Full legal name on large desktop screens */}
                    <span className="d-none d-xl-inline">ISAAC Logistic Solutions India Pvt. Ltd.</span>
                  </span>
                </div>
              </Link>
            </div>

            {/* DESKTOP LINKS */}
            <nav className="d-none d-lg-flex" style={{ height: '100%', alignItems: 'center' }}>
              <ul ref={ulRef} style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                listStyle: 'none', margin: 0, padding: 0, position: 'relative', height: '100%'
              }}>
                <Underline ulRef={ulRef} idx={hoverIdx ?? activeIdx} />

                {LINKS.map((link, i) => {
                  return (
                    <li key={link.path}
                      onMouseEnter={() => setHoverIdx(i)}
                      onMouseLeave={() => setHoverIdx(null)}
                      style={{ display: 'flex', alignItems: 'center', height: '100%', position: 'relative' }}
                    >
                      <NavLink to={link.path} end={link.path === '/'} className="nav-lnk"
                        style={({ isActive: a }) => ({
                          display: 'inline-flex', alignItems: 'center',
                          padding: '0.75rem 1rem', fontFamily: 'var(--font-heading)',
                          fontWeight: 600, fontSize: '0.82rem',
                          color: a ? 'var(--orange)' : 'rgba(255,255,255,0.75)',
                          textDecoration: 'none', transition: 'color 0.2s',
                          letterSpacing: '0.8px', textTransform: 'uppercase'
                        })}>
                        {link.label}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* RIGHT SIDE */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.2rem' }}>


              {/* CTA Button */}
              <Link to="/contact" className="btn-cta-nav d-none d-lg-inline-flex" style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                background: showSolid ? 'var(--orange)' : 'transparent',
                color: showSolid ? 'var(--navy)' : '#fff',
                border: '1px solid var(--orange)',
                fontFamily: 'var(--font-heading)', fontWeight: 700,
                fontSize: '0.72rem', padding: '0.55rem 1.3rem', borderRadius: 2,
                textDecoration: 'none', whiteSpace: 'nowrap', textTransform: 'uppercase',
                letterSpacing: '1px', transition: 'all 0.25s ease'
              }}>
                Get in Touch <FaArrowRight size={9} />
              </Link>

              {/* Mobile Burger */}
              <button className="d-lg-none"
                onClick={() => setMenuOpen(v => !v)}
                style={{
                  width: 34, height: 34, borderRadius: 2, cursor: 'pointer',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background 0.2s', flexShrink: 0
                }}>
                <Burger open={menuOpen} />
              </button>
            </div>

          </div>
        </div>
      </motion.header>

      {/* MOBILE OVERLAY */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            key="mob"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{   opacity: 0, y: -10 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            style={{
              position: 'fixed', top: NAV_H, left: 0, right: 0, bottom: 0,
              zIndex: 199, background: 'var(--navy-deep)',
              display: 'flex', flexDirection: 'column', overflow: 'hidden'
            }}
          >
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '2.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
                <div style={{
                  background: 'var(--purple-accent-dim)',
                  border: '1px solid var(--purple-accent-border)',
                  borderRadius: '30px',
                  padding: '0.4rem 1rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  backdropFilter: 'blur(4px)'
                }}>
                  <FaFemale size={12} color="var(--purple-accent-light)" />
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: '#fff',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    fontFamily: 'var(--font-heading)'
                  }}>
                    Women Empowered Entity
                  </span>
                </div>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 2rem' }}>
                {LINKS.map((link, i) => (
                  <li key={link.path}>
                    <NavLink to={link.path} end={link.path === '/'}
                      onClick={() => setMenuOpen(false)}
                      className="mob-nav-link"
                      style={({ isActive }) => ({
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '1rem 0', fontFamily: 'var(--font-heading)', fontWeight: 700,
                        fontSize: '1.4rem', color: isActive ? 'var(--orange)' : 'rgba(255,255,255,0.8)',
                        textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,0.05)',
                        transition: 'color 0.2s', textTransform: 'uppercase', letterSpacing: '1px'
                      })}>
                      <span>{link.label}</span>
                      <FaArrowRight size={12} color="var(--orange)" />
                    </NavLink>
                  </li>
                ))}
              </ul>

              <Link to="/contact" onClick={() => setMenuOpen(false)} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                background: 'var(--orange)', color: 'var(--navy)', fontFamily: 'var(--font-heading)',
                fontWeight: 700, fontSize: '0.85rem', padding: '1rem', borderRadius: 2,
                textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '1px'
              }}>
                Get In Touch <FaArrowRight size={11} />
              </Link>
            </div>

            <div style={{
              padding: '1.5rem 2.5rem 2.5rem', borderTop: '1px solid rgba(255,255,255,0.05)',
              display: 'flex', flexDirection: 'column', gap: '1rem'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                <a href="tel:+917010755885" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', textDecoration: 'none' }}>
                  <FaPhone size={9} color="var(--orange)" /> +91 70107 55885
                </a>
                <a href="mailto:info@isaacgroup.net" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', textDecoration: 'none' }}>
                  <FaEnvelope size={9} color="var(--orange)" /> info@isaacgroup.net
                </a>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.2rem' }}>
                {SOCIALS.map((s, i) => (
                  <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                    style={{
                      width: 28, height: 28, borderRadius: 2,
                      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'rgba(255,255,255,0.5)',
                    }}>
                    <s.icon size={10} />
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .nav-lnk:hover { color: var(--orange) !important; }
        .btn-cta-nav:hover { background: var(--orange) !important; color: var(--navy) !important; }
        .dd-item:hover { background: rgba(255,255,255,0.03) !important; border-color: var(--orange-border) !important; }
        .dd-item.green-hover:hover { border-color: rgba(16, 185, 129, 0.35) !important; }
        .topbar-social:hover { color: var(--orange) !important; }
        .mob-nav-link:hover { color: var(--orange) !important; }
      `}</style>
    </>
  );
};

export default Navbar;
